import React from "react";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import GenericButton from "@ui-components/Button/GenericButton";

export interface LoadMoreNotice {
  /** What is loaded and what is not, e.g. "500 of 1,200 loaded". */
  notice: string;
  /** e.g. "Load 500 more". */
  buttonLabel: string;
  isLoading: boolean;
  onLoadMore: () => void;
  /** Why the last page failed; the button becomes "Retry" and the rows stay. */
  loadMoreError?: string | null;
}

interface LogsTableToolbarProps {
  visibleCount: number;
  hiddenHeartbeats: number;
  hideHeartbeats: boolean;
  onHideHeartbeatsChange: (value: boolean) => void;
  groupRepeated: boolean;
  onGroupRepeatedChange: (value: boolean) => void;
  wrapMessages: boolean;
  onWrapMessagesChange: (value: boolean) => void;
  /** Left of the counters and switches (V1's attribute search). */
  leading?: React.ReactNode;
  /** Present only while the range has lines that are not loaded. */
  loadMore?: LoadMoreNotice;
}

// A failed page turns the button into Retry; it retries the same page.
function loadMoreLabel({ isLoading, loadMoreError, buttonLabel }: LoadMoreNotice): string {
  if (isLoading) return "Loading…";
  if (loadMoreError) return "Retry";
  return buttonLabel;
}

interface LabeledSwitchProps {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}

// GenericSwitch toggles on its own div's click and has no label, so the text
// gets its own click handler; it is not a <label>, which would re-click the
// hidden checkbox and undo the toggle.
function LabeledSwitch({ label, checked, onChange }: LabeledSwitchProps) {
  return (
    <div className="flex items-center gap-2 text-sm text-semantic-text-secondary">
      <GenericSwitch checked={checked} onChange={onChange} ariaLabel={label} />
      <span className="cursor-pointer select-none" onClick={() => onChange(!checked)}>
        {label}
      </span>
    </div>
  );
}

/**
 * Bar above the logs table: the counters of what is loaded, the three view
 * switches and, when the range holds more lines than were fetched, the notice
 * with its Load more button. The counters never speak for lines that are not
 * loaded; the notice says so.
 */
export function LogsTableToolbar({
  visibleCount,
  hiddenHeartbeats,
  hideHeartbeats,
  onHideHeartbeatsChange,
  groupRepeated,
  onGroupRepeatedChange,
  wrapMessages,
  onWrapMessagesChange,
  leading,
  loadMore,
}: LogsTableToolbarProps) {
  return (
    <div className="flex flex-col gap-2 px-4 pt-4 pb-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {leading && <div className="flex-1 min-w-[280px]">{leading}</div>}
        <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="text-sm text-semantic-text-secondary" aria-live="polite">
            {visibleCount.toLocaleString()} entries
            {hiddenHeartbeats > 0 &&
              ` · ${hiddenHeartbeats.toLocaleString()} heartbeats hidden`}
          </span>
          <LabeledSwitch
            label="Hide heartbeats"
            checked={hideHeartbeats}
            onChange={onHideHeartbeatsChange}
          />
          <LabeledSwitch
            label="Group repeated"
            checked={groupRepeated}
            onChange={onGroupRepeatedChange}
          />
          <LabeledSwitch
            label="Wrap messages"
            checked={wrapMessages}
            onChange={onWrapMessagesChange}
          />
        </div>
      </div>
      {loadMore && (
        <div className="flex items-center justify-end gap-3 text-sm text-semantic-text-secondary">
          {loadMore.loadMoreError && (
            <span role="alert" className="text-semantic-text-danger">
              {`Couldn't load more: ${loadMore.loadMoreError}`}
            </span>
          )}
          <span>{loadMore.notice}</span>
          <GenericButton
            label={loadMoreLabel(loadMore)}
            variant="secondary"
            size="sm"
            onClick={loadMore.onLoadMore}
            disabled={loadMore.isLoading}
          />
        </div>
      )}
    </div>
  );
}

export default LogsTableToolbar;
